import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  X,
  ChevronRight,
  ChevronLeft,
  Check,
  AlertCircle,
  Plus,
  FolderKanban,
  CreditCard,
  FolderGit,
} from "lucide-react";
import { Button } from "./ui/button";
import TableSkeleton from "./skeletons/TableSkeleton";
import { InlineLoadSkeleton } from "./common/InlineLoadSkeleton";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { projectService } from "../services/projectService";
import { userService } from "../services/userService";
import { rbacService } from "../services/rbacService";
import { useAuth } from "../context/AuthContext";
import { useModal } from "../context/ModalContext";
import { useCan } from "../hooks/useCan";
import { getStoredClientId } from "../utils/tokenUtils";
import toast from "react-hot-toast";
// DataTable not needed for this component

const STORAGE_KEY = "project_creation_wizard_state";

const PROJECT_STRUCTURES = [
  { value: "SINGLE_REPOSITORY", label: "Single Repository for all Cases" },
  { value: "MULTI_SUITE", label: "Multiple Test Suites to manage cases" },
  // SINGLE REPOSITORY WITH BASELINE SUPPORT - commented out
  // { value: 'BASELINE_ENABLED', label: 'Single Repository with Baseline support' }
];

const CreateProjectWizard = ({
  isOpen,
  onSuccess,
  clientId: propClientId,
  projectId: propProjectId,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { incrementModalCount, decrementModalCount } = useModal();
  const [currentStep, setCurrentStep] = useState(() =>
    searchParams.get("mode") === "add_user" ? 2 : 1,
  );
  const [creating, setCreating] = useState(false);

  // Get effective client ID
  const effectiveClientId =
    propClientId || getStoredClientId() || user?.client_id;
  const mode = searchParams.get("mode");
  const projectId = searchParams.get("project_id");

  // Step 1: Project Details
  const [projectName, setProjectName] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [structure, setStructure] = useState("");
  const [defaultTestCaseTemplateType, setDefaultTestCaseTemplateType] =
    useState("SINGLE_STEP");
  const [aiTestCaseGeneration, setAiTestCaseGeneration] = useState(false);
  const [testCaseApproval, setTestCaseApproval] = useState(false);
  const [projectNameError, setProjectNameError] = useState("");
  const [structureError, setStructureError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");

  // Step 2: Add Users
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [userRoles, setUserRoles] = useState({}); // { userId: roleId }
  const [usersLoading, setUsersLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [totalUsersCount, setTotalUsersCount] = useState(0);
  const [adminError, setAdminError] = useState("");

  // Step 2: Add & Assign New User modal
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUserFullName, setNewUserFullName] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserPhone, setNewUserPhone] = useState("");
  const [newUserClientRoleId, setNewUserClientRoleId] = useState("");
  const [newUserProjectRoleId, setNewUserProjectRoleId] = useState("");
  const [newUserInvite, setNewUserInvite] = useState(true);
  const [newUserErrors, setNewUserErrors] = useState({});
  const [newUserSubmitting, setNewUserSubmitting] = useState(false);
  const [newUserExistingWarning, setNewUserExistingWarning] = useState("");
  const [project, setProject] = useState(null);

  const isViewMode = useMemo(() => mode === "view", [mode]);
  const isEditMode = useMemo(() => mode === "edit", [mode]);
  const isAddUserMode = useMemo(() => mode === "add_user", [mode]);
  /** View mode: navigate both steps but no edits. */
  const isFieldsDisabled = isViewMode;

  // Permissions
  const { can: canCreateClientUser, loading: canCreateClientUserLoading } =
    useCan("user", "add_edit");

  // Templates - removed (layout selection no longer shown in project creation)

  // Auto-sync modal state with global context
  useEffect(() => {
    if (isOpen) {
      incrementModalCount();
      return () => {
        decrementModalCount();
      };
    }
  }, [isOpen, incrementModalCount, decrementModalCount]);

  useEffect(() => {
    localStorage.removeItem(STORAGE_KEY);
  }, []);
  // Load saved state from localStorage
  useEffect(() => {
    if (isEditMode) {
      setCurrentStep(1);
    } else if (isAddUserMode) {
      setCurrentStep(2);
    }
  }, [isEditMode, isAddUserMode]);

  useEffect(() => {
    if (isOpen) {
      const savedState = localStorage.getItem(STORAGE_KEY);
      if (savedState) {
        try {
          const state = JSON.parse(savedState);
          if (!isEditMode && !isAddUserMode) {
            setCurrentStep(state.currentStep || 1);
          }
          setProjectName(state.projectName || "");
          setProjectDescription(state.projectDescription || "");
          setStructure(state.structure || "");
          setDefaultTestCaseTemplateType(
            state.defaultTestCaseTemplateType || "MULTIPLE_STEPS",
          );
          setAiTestCaseGeneration(state.aiTestCaseGeneration || false);
          setTestCaseApproval(state.testCaseApproval || false);
          setSelectedUsers(new Set(state.selectedUsers || []));
          setUserRoles(state.userRoles || {});
        } catch (error) {
          console.error("Error loading saved state:", error);
        }
      }
    }
  }, [isOpen, isEditMode, isAddUserMode]);

  // Load roles
  const loadRoles = useCallback(async () => {
    if (!effectiveClientId) return;
    try {
      const response = await rbacService.getRoles({
        client_id: effectiveClientId,
      });
      if (response.success && response.data?.roles) {
        setRoles(response.data.roles);
      }
    } catch (error) {
      console.error("Error loading roles:", error);
      toast.error("Failed to load roles");
    }
  }, [effectiveClientId]);

  useEffect(() => {
    (async () => {
      if (
        projectId &&
        (mode === "view" || mode === "edit" || mode === "add_user")
      ) {
        const response = await projectService.getProject(
          projectId,
          effectiveClientId,
        );
        if (response.success && response.data) {
          setProject(response.data);
          setProjectName(response.data.name || "");
          setProjectDescription(response.data.description || "");
          setStructure(response.data.structure || "");
          setDefaultTestCaseTemplateType(
            response.data.default_test_case_template_type || "SINGLE_STEP",
          );
          setAiTestCaseGeneration(
            response.data.ai_test_case_generation || false,
          );
          setTestCaseApproval(response.data.test_case_approval || false);
          setSelectedUsers(new Set(response.data.selected_users || []));
          setUserRoles(response.data.user_roles || {});
        } else {
          toast.error(response.message || "Failed to load project");
        }
      }
    })();
  }, [mode, effectiveClientId, projectId]);

  // Load existing project members if editing
  const loadExistingProjectMembers = useCallback(async () => {
    if (!projectId || !effectiveClientId) return;

    try {
      const response = await projectService.getProjectUsers(
        projectId,
        effectiveClientId,
      );
      if (response.success && response.data?.users) {
        const existingMembers = response.data.users;
        const existingSelected = new Set();
        const existingUserRoles = {};

        existingMembers.forEach((member) => {
          existingSelected.add(member.user_id);
          if (member.role_id) {
            existingUserRoles[member.user_id] = member.role_id;
          }
        });

        // Merge with existing selections (don't overwrite if user already selected)
        setSelectedUsers((prev) => {
          const merged = new Set(prev);
          existingSelected.forEach((userId) => merged.add(userId));
          return merged;
        });

        setUserRoles((prev) => ({
          ...prev,
          ...existingUserRoles,
        }));
      }
    } catch (error) {
      console.error("Error loading existing project members:", error);
      // Don't show error toast as this is optional
    }
  }, [projectId, effectiveClientId]);

  // Load users with pagination
  const loadUsers = useCallback(
    async (page = 1, search = "") => {
      if (!effectiveClientId) return;

      try {
        setUsersLoading(true);
        const filters = {
          client_id: effectiveClientId,
          is_active: true,
          is_deleted: false,
        };

        if (search.trim()) {
          filters.search = search.trim();
        }

        const response = await userService.findUsers(
          filters,
          page,
          usersPerPage,
        );

        if (response.success && response.data) {
          const usersList = response.data.users || [];
          const totalCount = response.data.count || 0;

          setUsers(usersList);
          setTotalUsersCount(totalCount);

          // Load roles if not already loaded
          if (roles.length === 0) {
            await loadRoles();
          }

          // Use roles from state if available, otherwise fetch
          let rolesList = roles.length > 0 ? roles : [];
          if (rolesList.length === 0) {
            const rolesResponse = await rbacService.getRoles({
              client_id: effectiveClientId,
            });
            rolesList =
              rolesResponse.success && rolesResponse.data?.roles
                ? rolesResponse.data.roles
                : [];
            if (rolesList.length > 0) {
              setRoles(rolesList);
            }
          }

          // Set default roles for newly visible users (only if not already set)
          setUserRoles((prev) => {
            const updated = { ...prev };
            usersList.forEach((user) => {
              // Only set default if not already set and user is selected
              if (!updated[user.user_id] && selectedUsers.has(user.user_id)) {
                const userRoleName = user.role_name || user.role;
                const userRole = rolesList.find(
                  (r) =>
                    r.role_name.toLowerCase() === userRoleName?.toLowerCase(),
                );
                if (userRole) {
                  updated[user.user_id] = userRole.role_id;
                }
              }
            });
            return updated;
          });
        }
      } catch (error) {
        console.error("Error loading users:", error);
        toast.error("Failed to load users");
      } finally {
        setUsersLoading(false);
      }
    },
    [effectiveClientId, usersPerPage, roles, selectedUsers, loadRoles],
  );

  // Load existing project members when modal opens (if editing)
  useEffect(() => {
    if (isOpen && projectId && effectiveClientId) {
      loadExistingProjectMembers();
    }
  }, [isOpen, projectId, effectiveClientId, loadExistingProjectMembers]);

  // Load users when step 2 is active or page changes
  useEffect(() => {
    if (isOpen && effectiveClientId && currentStep === 2) {
      loadUsers(currentPage, searchQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, effectiveClientId, currentStep, currentPage]);

  // Debounce search query - reset to page 1 and reload
  useEffect(() => {
    if (
      isOpen &&
      effectiveClientId &&
      currentStep === 2 &&
      searchQuery !== undefined
    ) {
      const timeoutId = setTimeout(() => {
        if (currentPage !== 1) {
          setCurrentPage(1);
        } else {
          loadUsers(1, searchQuery);
        }
      }, 500); // 500ms debounce

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Load roles when step 2 is active
  useEffect(() => {
    if (
      isOpen &&
      effectiveClientId &&
      currentStep === 2 &&
      roles.length === 0
    ) {
      loadRoles();
    }
  }, [isOpen, effectiveClientId, currentStep, roles.length, loadRoles]);

  // Show/hide "At least one user must be selected" error based on user selection
  useEffect(() => {
    if (currentStep === 2) {
      if (selectedUsers.size > 0) {
        // Clear "at least one user" error when users are selected
        // But preserve other error messages (like role validation errors)
        if (adminError === "At least one user must be selected") {
          setAdminError("");
        }
      } else if (selectedUsers.size === 0) {
        // Show error when no users are selected
        // Only set if there's no other error message showing
        if (
          !adminError ||
          adminError === "At least one user must be selected"
        ) {
          setAdminError("At least one user must be selected");
        }
      }
    }
  }, [selectedUsers.size, currentStep, adminError]);

  // Save state to localStorage
  const saveState = () => {
    const state = {
      currentStep,
      projectName,
      projectDescription,
      structure,
      aiTestCaseGeneration,
      testCaseApproval,
      selectedUsers: Array.from(selectedUsers),
      userRoles,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  };

  // Clear saved state
  const clearState = () => {
    localStorage.removeItem(STORAGE_KEY);
    setCurrentStep(1);
    setProjectName("");
    setProjectDescription("");
    setStructure("");
    setDefaultTestCaseTemplateType("MULTIPLE_STEPS");
    setAiTestCaseGeneration(false);
    setTestCaseApproval(false);
    setSelectedUsers(new Set());
    setUserRoles({});
    setProjectNameError("");
    setStructureError("");
    setDescriptionError("");
    setAdminError("");
  };

  // Validate Step 1
  const validateStep1 = () => {
    setProjectNameError("");
    setStructureError("");
    setDescriptionError("");

    if (!projectName.trim()) {
      setProjectNameError("Project name is required");
      return false;
    }

    if (projectName.trim().length < 3) {
      setProjectNameError("Project name must be at least 3 characters");
      return false;
    }

    if (!structure) {
      setStructureError("Project type is required");
      return false;
    }

    if (projectDescription && projectDescription.length > 300) {
      setDescriptionError("Description must not exceed 300 characters");
      return false;
    }

    return true;
  };

  // Validate Step 2
  const validateStep2 = () => {
    // Only clear adminError if we're not showing the "at least one user" message
    // This allows the message to persist until users are selected
    if (adminError !== "At least one user must be selected") {
      setAdminError("");
    }

    if (selectedUsers.size === 0) {
      setAdminError("At least one user must be selected");
      return false;
    }

    // Clear the "at least one user" error if users are now selected
    if (adminError === "At least one user must be selected") {
      setAdminError("");
    }

    // Check if all selected users have a project role assigned
    const usersWithoutRoles = Array.from(selectedUsers).filter(
      (userId) => !userRoles[userId],
    );
    if (usersWithoutRoles.length > 0) {
      // Try to get user names from current page, otherwise show count
      const userNames = usersWithoutRoles
        .map((userId) => {
          const user = users.find((u) => u.user_id === userId);
          return user?.full_name;
        })
        .filter((name) => name); // Remove undefined values

      if (userNames.length > 0) {
        const remainingCount = usersWithoutRoles.length - userNames.length;
        let errorMsg = `Project role is required for all selected users. Please assign a role to: ${userNames.join(", ")}`;
        if (remainingCount > 0) {
          errorMsg += ` and ${remainingCount} other user${remainingCount > 1 ? "s" : ""}`;
        }
        setAdminError(errorMsg);
      } else {
        setAdminError(
          `Project role is required for all selected users. ${usersWithoutRoles.length} user${usersWithoutRoles.length > 1 ? "s" : ""} ${usersWithoutRoles.length > 1 ? "are" : "is"} missing a project role.`,
        );
      }
      return false;
    }

    // Check if at least one user has an admin role
    const hasAdmin = Array.from(selectedUsers).some((userId) => {
      const roleId = userRoles[userId];
      if (!roleId) return false;
      const role = roles.find((r) => r.role_id === roleId);
      return (
        role &&
        (role.role_name.toLowerCase().includes("admin") ||
          role.role_name.toLowerCase() === "admin" ||
          role.role_name.toLowerCase().includes("project admin"))
      );
    });

    if (!hasAdmin) {
      setAdminError(
        "Select at least one Project Admin. Users without project access must not see project data.",
      );
      return false;
    }

    return true;
  };

  const handleCancelToProjects = () => {
    clearState();
    navigate(
      `/projects${effectiveClientId ? `?client_id=${effectiveClientId}` : ""}`,
    );
  };

  // Handle Step 1 Next
  const handleStep1Next = async () => {
    if (isViewMode) {
      setCurrentStep(2);
      return;
    }
    if (!validateStep1()) {
      return;
    }
    if (isEditMode) {
      try {
        const projectTypeMap = {
          SINGLE_REPOSITORY: "Single Repository for all Cases",
          MULTI_SUITE: "Multiple Test Suites to manage cases",
          // 'BASELINE_ENABLED': 'Single Repository with Baseline support' // commented out
        };
        const payload = {
          name: projectName.trim(),
          description: projectDescription.trim() || null,
          project_type:
            projectTypeMap[structure] || "Single Repository for all Cases",
          structure: structure,
          test_case_structure_type: defaultTestCaseTemplateType, // Test case structure type (SINGLE_STEP, MULTIPLE_STEPS, BDD)
          ai_test_case_generation: aiTestCaseGeneration,
          test_case_approval: testCaseApproval,
          client_id: effectiveClientId,
          project_id: projectId,
        };
        const response = await projectService.updateProject(payload);
        if (response.success) {
          toast.success("Project updated successfully");
          navigate(`/projects?client_id=${effectiveClientId}`);
          clearState();
        } else {
          toast.error(response.message || "Failed to update project");
        }
      } catch (error) {
        console.error("Error updating project:", error);
        toast.error(
          error.response?.data?.message || "Failed to update project",
        );
      }
      return;
    }
    setCurrentStep(2);
    // Users and roles will be loaded by useEffect when step changes
  };

  // Handle Step 2 Back
  const handleStep2Back = () => {
    if (isAddUserMode) {
      handleCancelToProjects();
      return;
    }
    saveState();
    setCurrentStep(1);
  };

  // Handle user selection
  const handleUserToggle = (userId) => {
    if (isViewMode) return;
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
      const newUserRoles = { ...userRoles };
      delete newUserRoles[userId];
      setUserRoles(newUserRoles);
    } else {
      newSelected.add(userId);
      // Set default role for user
      const user = users.find((u) => u.user_id === userId);
      if (user) {
        // Check both role_name and role for backward compatibility
        const userRoleName = user.role_name || user.role;
        const defaultRole = roles.find(
          (r) => r.role_name.toLowerCase() === userRoleName?.toLowerCase(),
        );
        if (defaultRole) {
          setUserRoles((prev) => ({
            ...prev,
            [userId]: defaultRole.role_id,
          }));
        }
      }
    }
    setSelectedUsers(newSelected);
  };

  // Handle select all visible (current page users)
  const handleSelectAllVisible = () => {
    if (isViewMode) return;
    const paginatedUsers = getPaginatedUsers();
    const allSelected =
      paginatedUsers.length > 0 &&
      paginatedUsers.every((u) => selectedUsers.has(u.user_id));

    if (allSelected) {
      // Deselect all visible
      const newSelected = new Set(selectedUsers);
      const newUserRoles = { ...userRoles };
      paginatedUsers.forEach((u) => {
        newSelected.delete(u.user_id);
        delete newUserRoles[u.user_id];
      });
      setSelectedUsers(newSelected);
      setUserRoles(newUserRoles);
    } else {
      // Select all visible
      const newSelected = new Set(selectedUsers);
      paginatedUsers.forEach((u) => {
        newSelected.add(u.user_id);
        // Set default role if not already set
        if (!userRoles[u.user_id]) {
          const userRoleName = u.role_name || u.role;
          const defaultRole = roles.find(
            (r) => r.role_name.toLowerCase() === userRoleName?.toLowerCase(),
          );
          if (defaultRole) {
            setUserRoles((prev) => ({
              ...prev,
              [u.user_id]: defaultRole.role_id,
            }));
          }
        }
      });
      setSelectedUsers(newSelected);
    }
  };

  // Handle role change for user
  const handleRoleChange = (userId, roleId) => {
    if (isViewMode) return;
    setUserRoles((prev) => ({
      ...prev,
      [userId]: roleId,
    }));
  };

  // Get paginated users (already paginated from API)
  const getPaginatedUsers = () => {
    return users;
  };

  // Get total pages based on server-side count
  const getTotalPages = () => {
    return Math.ceil(totalUsersCount / usersPerPage);
  };

  // Handle create project
  const handleCreateProject = async () => {
    if (!validateStep2()) {
      return;
    }

    if (isAddUserMode && projectId) {
      setCreating(true);
      try {
        const members = Array.from(selectedUsers)
          .filter((userId) => userRoles[userId])
          .map((userId) => ({
            user_id: userId,
            role_id: userRoles[userId],
          }));

        const response = await projectService.assignProjectUsers(
          projectId,
          effectiveClientId,
          members,
        );

        if (response.success) {
          clearState();
          toast.success("Project users updated successfully");
          navigate(
            `/projects${effectiveClientId ? `?client_id=${effectiveClientId}` : ""}`,
            { replace: true },
          );
        } else {
          toast.error(response.message || "Failed to update project users");
        }
      } catch (error) {
        console.error("Error assigning users:", error);
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to update project users",
        );
      } finally {
        setCreating(false);
      }
      return;
    }

    setCreating(true);
    try {
      // Step 1: Create project
      // Map structure to project_type for backward compatibility
      const projectTypeMap = {
        SINGLE_REPOSITORY: "Single Repository for all Cases",
        MULTI_SUITE: "Multiple Test Suites to manage cases",
        // 'BASELINE_ENABLED': 'Single Repository with Baseline support' // commented out
      };

      const projectData = {
        name: projectName.trim(),
        description: projectDescription.trim() || null,
        project_type:
          projectTypeMap[structure] || "Single Repository for all Cases",
        structure: structure,
        test_case_structure_type: defaultTestCaseTemplateType, // Test case structure type (SINGLE_STEP, MULTIPLE_STEPS, BDD)
        ai_test_case_generation: aiTestCaseGeneration,
        test_case_approval: testCaseApproval,
        client_id: effectiveClientId,
      };

      const createResponse = await projectService.createProject(projectData);

      if (!createResponse.success) {
        throw new Error(createResponse.message || "Failed to create project");
      }

      const projectId = createResponse.data?.project_id;
      if (!projectId) {
        throw new Error("Project ID not returned from server");
      }

      // Step 2: Assign users to project
      const members = Array.from(selectedUsers)
        .filter((userId) => userRoles[userId]) // Only include users with roles
        .map((userId) => ({
          user_id: userId,
          role_id: userRoles[userId],
        }));

      if (members.length > 0) {
        try {
          await projectService.assignProjectUsers(
            projectId,
            effectiveClientId,
            members,
          );
        } catch (assignError) {
          console.error("Error assigning users:", assignError);
          // Don't fail project creation if user assignment fails
          toast.error(
            "Project created but failed to assign users. Please assign users manually.",
          );
        }
      }

      clearState();
      toast.success("Project created successfully!");
      if (onSuccess) {
        onSuccess(createResponse.data);
      } else {
        navigate(
          `/projects${effectiveClientId ? `?client_id=${effectiveClientId}` : ""}`,
          { replace: true },
        );
      }
    } catch (error) {
      console.error("Error creating project:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to create project";
      toast.error(errorMessage);

      // Handle specific error codes
      if (
        error.response?.data?.error === "Structure required" ||
        error.response?.data?.error === "Invalid structure"
      ) {
        setCurrentStep(1);
        setStructureError(errorMessage);
      }
    } finally {
      setCreating(false);
    }
  };

  // ===== New User Modal Logic (Step 2) =====

  const resetNewUserForm = () => {
    setNewUserFullName("");
    setNewUserEmail("");
    setNewUserPhone("");
    setNewUserClientRoleId("");
    setNewUserProjectRoleId("");
    setNewUserInvite(true);
    setNewUserErrors({});
    setNewUserExistingWarning("");
  };

  const validateNewUserForm = () => {
    const errors = {};
    const emailTrimmed = newUserEmail.trim();
    const nameTrimmed = newUserFullName.trim();

    if (!nameTrimmed) {
      errors.full_name = "Full name is required";
    }

    if (!emailTrimmed) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) {
      errors.email = "Invalid email format";
    }

    if (!newUserClientRoleId) {
      errors.client_role = "Client role is required";
    }

    if (!newUserProjectRoleId) {
      errors.project_role = "Project role is required";
    }

    setNewUserErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOpenNewUserModal = () => {
    if (isViewMode || !canCreateClientUser) {
      return;
    }
    resetNewUserForm();
    setShowAddUserModal(true);
    incrementModalCount();
  };

  const handleCloseNewUserModal = () => {
    setShowAddUserModal(false);
    resetNewUserForm();
    decrementModalCount();
  };

  const handleAddAndAssignNewUser = async () => {
    if (!effectiveClientId) {
      toast.error("Client context is missing. Please refresh and try again.");
      return;
    }

    if (!validateNewUserForm()) {
      return;
    }

    const emailTrimmed = newUserEmail.trim().toLowerCase();
    const nameTrimmed = newUserFullName.trim();

    try {
      setNewUserSubmitting(true);
      setNewUserExistingWarning("");

      // Step 1: Check for existing user by email to prevent duplicates
      let existingUser = null;
      try {
        const findResponse = await userService.findUsers(
          {
            client_id: effectiveClientId,
            is_active: true,
            is_deleted: false,
            search: emailTrimmed,
          },
          1,
          5,
        );
        if (findResponse?.success && findResponse.data?.users?.length) {
          existingUser = findResponse.data.users.find(
            (u) => u.email?.toLowerCase() === emailTrimmed,
          );
        }
      } catch (searchError) {
        console.error(
          "Error checking for existing user by email:",
          searchError,
        );
        // Continue to creation attempt; backend will still protect against duplicates
      }

      let userId;
      let userRecord;

      if (existingUser) {
        // Do not recreate user, just reuse existing
        userId = existingUser.user_id;
        userRecord = existingUser;
        setNewUserExistingWarning(
          "A user with this email already exists. They have been added to this project.",
        );
      } else {
        // Step 2: Create new client-level user
        const clientRole = roles.find((r) => r.role_id === newUserClientRoleId);
        const clientRoleName = clientRole?.role_name;

        const userPayload = {
          full_name: nameTrimmed,
          email: emailTrimmed,
          client_id: effectiveClientId,
          // Backend createUser expects a role name string; map from selected client role
          role: clientRoleName,
        };

        if (newUserPhone.trim()) {
          userPayload.phone = newUserPhone.trim();
        }
        // Optional invite toggle - backend can ignore if unsupported
        userPayload.send_invite = !!newUserInvite;

        const createResponse = await userService.createUser(userPayload);
        const created =
          createResponse?.data?.user ||
          createResponse?.data ||
          createResponse?.user ||
          createResponse;

        userId = created?.user_id;
        userRecord = created;

        if (!userId) {
          throw new Error("User ID not returned from create user API");
        }

        // Step 3: Explicitly assign client role to the user
        if (newUserClientRoleId) {
          try {
            await rbacService.assignRoleToUser(
              userId,
              newUserClientRoleId,
              effectiveClientId,
              null,
            );
          } catch (assignClientRoleError) {
            console.error(
              "Error assigning client role to new user:",
              assignClientRoleError,
            );
            toast.error(
              "User was created but assigning the selected client role failed. Please update roles from User Management.",
            );
          }
        }
      }

      if (!userId) {
        throw new Error("Unable to determine user ID for assignment");
      }

      // Normalize user record shape for table if needed
      const finalUser = userRecord ||
        users.find((u) => u.user_id === userId) || {
          user_id: userId,
          full_name: nameTrimmed,
          email: emailTrimmed,
        };

      // Step 4: Immediately reflect in current project assignment state
      setSelectedUsers((prev) => {
        const updated = new Set(prev);
        updated.add(userId);
        return updated;
      });

      setUserRoles((prev) => ({
        ...prev,
        [userId]: newUserProjectRoleId,
      }));

      // Ensure user appears in the current table/search & select-all logic
      try {
        await loadUsers(currentPage, searchQuery);
      } catch (reloadError) {
        console.error("Error reloading users after add/assign:", reloadError);
        // Fallback: optimistically add user to current list
        setUsers((prev) => {
          if (prev.some((u) => u.user_id === finalUser.user_id)) {
            return prev;
          }
          return [finalUser, ...prev];
        });
        setTotalUsersCount((prev) => prev + 1);
      }

      toast.success(
        existingUser
          ? "Existing user assigned to this project."
          : "User created and added to this project.",
      );

      handleCloseNewUserModal();
    } catch (error) {
      console.error(
        "Error adding and assigning user from project wizard:",
        error,
      );
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to add and assign user";
      toast.error(errorMessage);
    } finally {
      setNewUserSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const paginatedUsers = getPaginatedUsers();
  const totalPages = getTotalPages();
  const allVisibleSelected =
    paginatedUsers.length > 0 &&
    paginatedUsers.every((u) => selectedUsers.has(u.user_id));

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between p-2 border-b">
        <h2 className="text-2xl font-semibold text-gray-900">Create Project</h2>
        {/* <button
            onClick={() => {
              clearState();
              onClose();
            }}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button> */}
      </div>

      {/* Step Indicator */}
      <div className="flex items-center justify-center p-3 border-b bg-gray-50">
        {(isEditMode ? [1] : isAddUserMode ? [2] : [1, 2]).map(
          (step, stepIndex, steps) => (
            <React.Fragment key={step}>
              <div className="flex items-center">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center font-semibold ${
                    currentStep > step
                      ? "bg-green-500 text-white"
                      : currentStep === step
                        ? "bg-purple-600 text-white"
                        : "bg-gray-300 text-gray-600"
                  }`}
                >
                  {currentStep > step ? <Check className="w-6 h-6" /> : step}
                </div>
                <span className="ml-2 text-sm font-medium">
                  {step === 1 && "Project Details"}
                  {step === 2 && "Add Users"}
                </span>
                {currentStep === step && (
                  <span className="ml-2 text-xs text-gray-500">
                    {step === 1 ? "Active" : step === 2 ? "Active" : ""}
                  </span>
                )}
              </div>
              {stepIndex < steps.length - 1 && (
                <div
                  className={`w-16 h-1 mx-4 ${
                    currentStep > step ? "bg-green-500" : "bg-gray-300"
                  }`}
                />
              )}
            </React.Fragment>
          ),
        )}
      </div>

      {/* Step Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {currentStep === 1 && (
          <div className="space-y-8">
            {/* Project Information */}
            <div className="bg-[#f3eeee] rounded-2xl border border-[#e7dede] p-7 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <FolderGit
                  className="w-6 h-6"
                  style={{
                    color: "rgb(236 72 153 / var(--tw-border-opacity, 1))",
                  }}
                />

                <h3 className="text-lg font-semibold text-gray-900">
                  Project General Details
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                {/* Project Name */}
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="projectName" className="text-gray-700">
                      Project Name <span className="text-red-500">*</span>
                    </Label>
                    <p className="text-xs text-gray-500">0/50 characters</p>
                  </div>

                  <Input
                    id="projectName"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    placeholder="Project Name"
                    disabled={isFieldsDisabled}
                    className={`mt-2 h-11 rounded-xl border bg-white px-4 text-sm shadow-sm focus-visible:ring-2 focus-visible:ring-pink-200 disabled:cursor-not-allowed disabled:opacity-60 ${
                      projectNameError ? "border-red-300" : "border-gray-200"
                    }`}
                  />

                  {projectNameError && (
                    <p className="mt-2 text-sm text-red-500">
                      {projectNameError}
                    </p>
                  )}

                  <p className="mt-3 text-xs text-gray-600 text-center leading-relaxed">
                    Project name must be unique within the client.
                    <br />
                    Ex: Banking Software, Mobile Application or Web Application
                    Software.
                  </p>
                </div>

                {/* Project Description */}
                <div>
                  <div className="flex items-center justify-between">
                    <Label
                      htmlFor="projectDescription"
                      className="text-gray-700"
                    >
                      Project Description
                    </Label>
                    <p className="text-xs text-gray-500">
                      {projectDescription.length}/300 characters
                    </p>
                  </div>

                  <Textarea
                    id="projectDescription"
                    value={projectDescription}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 300) {
                        setProjectDescription(value);
                        if (descriptionError) {
                          setDescriptionError("");
                        }
                      }
                    }}
                    placeholder="Project Description"
                    rows={4}
                    disabled={isFieldsDisabled}
                    className={`mt-2 rounded-xl border bg-white px-4 py-3 text-sm shadow-sm focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-60 ${
                      descriptionError
                        ? "border-red-300 focus-visible:ring-red-200"
                        : "border-[#bca7ff] focus-visible:ring-[#d7ccff]"
                    }`}
                    maxLength={300}
                  />

                  <div className="flex items-center justify-between mt-2">
                    {descriptionError && (
                      <p className="text-sm text-red-500">{descriptionError}</p>
                    )}
                    <p
                      className={`text-xs ml-auto ${
                        projectDescription.length > 300
                          ? "text-red-500"
                          : "text-gray-500"
                      }`}
                    >
                      {/* already shown on top right, keeping this minimal */}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Project Type + Test Case Template */}
            <div className="bg-[#f3eeee] rounded-2xl border border-[#e7dede] p-7 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <FolderKanban
                  className="w-6 h-6"
                  style={{
                    color: "rgb(236 72 153 / var(--tw-border-opacity, 1))",
                  }}
                />
                <h3 className="text-lg font-semibold text-gray-900">
                  Project Type and Test Case Template
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                {/* Project Type */}
                <div>
                  <Label htmlFor="structure" className="text-gray-700">
                    Project Type <span className="text-red-500">*</span>
                  </Label>

                  <Select
                    value={structure}
                    onValueChange={setStructure}
                    disabled={isEditMode || isFieldsDisabled}
                  >
                    <SelectTrigger
                      className={`mt-2 h-11 rounded-xl border bg-white px-4 text-sm shadow-sm focus:ring-2 focus:ring-pink-200 disabled:cursor-not-allowed disabled:opacity-60 ${
                        structureError ? "border-red-300" : "border-gray-200"
                      }`}
                    >
                      <SelectValue placeholder="Select project type" />
                    </SelectTrigger>

                    <SelectContent>
                      {PROJECT_STRUCTURES.map((struct) => (
                        <SelectItem key={struct.value} value={struct.value}>
                          {struct.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {structureError && (
                    <p className="mt-2 text-sm text-red-500">
                      {structureError}
                    </p>
                  )}

                  <p className="mt-3 text-xs text-gray-600 text-center">
                    Project type cannot be changed after creation.{" "}
                    <span className="text-red-500 font-medium">
                      Choose carefully.
                    </span>
                  </p>
                </div>

                {/* Test Case Template */}
                <div>
                  <Label
                    htmlFor="defaultTestCaseTemplateType"
                    className="text-gray-700"
                  >
                    Test Case Template <span className="text-red-500">*</span>
                  </Label>

                  <Select
                    value={defaultTestCaseTemplateType}
                    onValueChange={setDefaultTestCaseTemplateType}
                    disabled={isEditMode || isFieldsDisabled}
                  >
                    <SelectTrigger className="mt-2 h-11 rounded-xl border border-gray-200 bg-white px-4 text-sm shadow-sm focus:ring-2 focus:ring-pink-200 disabled:cursor-not-allowed disabled:opacity-60">
                      <SelectValue placeholder="Select Template" />
                    </SelectTrigger>

                    <SelectContent>
                      <SelectItem value="SINGLE_STEP">Single Step</SelectItem>
                      <SelectItem value="MULTIPLE_STEPS">
                        Multiple Steps
                      </SelectItem>
                      <SelectItem value="BDD">
                        BDD (Behavior-Driven Development)
                      </SelectItem>
                    </SelectContent>
                  </Select>

                  <p className="mt-3 text-xs text-gray-600 text-center">
                    A standard format to write and execute test cases
                    consistently.
                  </p>
                </div>
              </div>
            </div>

            {/* Feature Toggles */}
            <div className="bg-[#f3eeee] rounded-2xl border border-[#e7dede] p-7 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <CreditCard
                  className="w-6 h-6"
                  style={{
                    color: "rgb(236 72 153 / var(--tw-border-opacity, 1))",
                  }}
                />
                <h3 className="text-lg font-semibold text-gray-900">
                  Feature Toggles
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                {/* Toggle 1 */}
                <div className="flex items-start gap-4">
                  <Checkbox
                    id="aiTestCaseGeneration"
                    checked={aiTestCaseGeneration}
                    onCheckedChange={setAiTestCaseGeneration}
                    disabled={isFieldsDisabled}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <Label
                      htmlFor="aiTestCaseGeneration"
                      className={`font-medium text-xl text-gray-900 ${isFieldsDisabled ? "cursor-default" : "cursor-pointer"}`}
                    >
                      Enable Test Case Generation with AI
                    </Label>
                    <p className="text-sm text-gray-600 mt-2">
                      A standard format to write and execute test cases
                      consistently with AI.
                    </p>
                  </div>
                </div>

                {/* Toggle 2 */}
                <div className="flex items-start gap-4">
                  <Checkbox
                    id="testCaseApproval"
                    checked={testCaseApproval}
                    onCheckedChange={setTestCaseApproval}
                    disabled={isFieldsDisabled}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <Label
                      htmlFor="testCaseApproval"
                      className={`font-medium text-xl text-gray-900 ${isFieldsDisabled ? "cursor-default" : "cursor-pointer"}`}
                    >
                      Enable Test Case Approval Feature
                    </Label>
                    <p className="text-sm text-gray-600 mt-2">
                      All test cases require approval before getting active.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="space-y-4">
            {/* Info Box */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-purple-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-purple-900">
                  Assign users to this project with project-specific roles.
                  Project roles are independent of client-level roles.
                </p>
              </div>
            </div>

            {/* Search and Select All */}
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <Input
                  type="text"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  disabled={isFieldsDisabled}
                  className="pl-10 disabled:cursor-not-allowed disabled:opacity-60"
                />
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="selectAllVisible"
                  checked={allVisibleSelected}
                  onCheckedChange={handleSelectAllVisible}
                  disabled={isFieldsDisabled}
                />
                <Label
                  htmlFor="selectAllVisible"
                  className={
                    isFieldsDisabled ? "cursor-default" : "cursor-pointer"
                  }
                >
                  Select All Visible
                </Label>
              </div>
              {!canCreateClientUserLoading && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleOpenNewUserModal}
                  disabled={!canCreateClientUser || isFieldsDisabled}
                  title={
                    !canCreateClientUser
                      ? "You do not have permission to add client users."
                      : ""
                  }
                  className="whitespace-nowrap"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add New User
                </Button>
              )}
            </div>

            {/* Pagination Info */}
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>
                {selectedUsers.size} user{selectedUsers.size !== 1 ? "s" : ""}{" "}
                selected
              </span>
              {totalUsersCount > 0 && (
                <span>
                  Page {currentPage} of {getTotalPages()} ({totalUsersCount}{" "}
                  total users)
                </span>
              )}
            </div>

            {/* Users Table */}
            {usersLoading ? (
              <div className="py-4">
                <TableSkeleton rows={6} columns={4} />
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        <Checkbox
                          checked={allVisibleSelected}
                          onCheckedChange={handleSelectAllVisible}
                          disabled={isFieldsDisabled}
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        User Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Email
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Client Role
                      </th>
                      {selectedUsers.size > 0 && (
                        <>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Project Role
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Actions
                          </th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {paginatedUsers.map((user) => {
                      const isSelected = selectedUsers.has(user.user_id);
                      const userRoleId = userRoles[user.user_id];

                      return (
                        <tr key={user.user_id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() =>
                                handleUserToggle(user.user_id)
                              }
                              disabled={isFieldsDisabled}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                                <span className="text-sm font-medium text-purple-700">
                                  {user.full_name?.charAt(0)?.toUpperCase() ||
                                    "U"}
                                </span>
                              </div>
                              <span className="text-sm text-gray-900">
                                {user.full_name}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {user.email}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {user.role_name || user.role || "N/A"}
                          </td>
                          {selectedUsers.size > 0 && (
                            <>
                              <td className="px-4 py-3">
                                {isSelected ? (
                                  <Select
                                    value={userRoleId || undefined}
                                    onValueChange={(roleId) =>
                                      handleRoleChange(user.user_id, roleId)
                                    }
                                    disabled={isFieldsDisabled}
                                  >
                                    <SelectTrigger
                                      className={`w-40 disabled:cursor-not-allowed disabled:opacity-60 ${!userRoleId ? "border-red-300 focus:border-red-300" : ""}`}
                                    >
                                      <SelectValue placeholder="Select role *" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {roles.map((role) => (
                                        <SelectItem
                                          key={role.role_id}
                                          value={role.role_id}
                                        >
                                          {role.role_name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <span className="text-sm text-gray-400">
                                    -
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                {isSelected &&
                                  (isFieldsDisabled ? (
                                    <span className="text-sm text-gray-400">
                                      —
                                    </span>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleUserToggle(user.user_id)
                                      }
                                      className="text-red-500 hover:text-red-700"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  ))}
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination Controls */}
            {totalUsersCount > usersPerPage && (
              <div className="flex items-center justify-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newPage = Math.max(1, currentPage - 1);
                    setCurrentPage(newPage);
                  }}
                  disabled={currentPage === 1 || usersLoading}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                <span className="text-sm text-gray-600">
                  Page {currentPage} of {getTotalPages()} ({totalUsersCount}{" "}
                  total users)
                </span>
                <Button
                  size="sm"
                  variant="main"
                  style={{
                    background: "#2E9E6F",
                    color: "white",
                  }}
                  onClick={() => {
                    const newPage = Math.min(getTotalPages(), currentPage + 1);
                    setCurrentPage(newPage);
                  }}
                  disabled={currentPage >= getTotalPages() || usersLoading}
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}

            {/* Info Text */}
            <p className="text-xs text-gray-500 mt-4">
              Roles are sourced from Client Role Management.
            </p>

            {/* Admin Error */}
            {adminError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                <p className="text-sm text-red-900">{adminError}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="flex items-center justify-between p-6 border-t bg-gray-50">
        <div>
          {currentStep === 2 && (
            <Button
              variant="outline"
              onClick={handleStep2Back}
              disabled={creating}
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              {isAddUserMode ? "Back to Projects" : "Back to Project Details"}
            </Button>
          )}
        </div>
        <div className="flex items-center space-x-3">
          {/* <Button
              variant="outline"
              onClick={() => {
                clearState();
                onClose();
              }}
              disabled={creating}
            >
              Cancel
            </Button> */}
          {currentStep === 1 && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancelToProjects}
                disabled={creating}
              >
                Cancel
              </Button>
              <Button
                variant="main"
                style={{
                  background: "#2E9E6F",
                  color: "white",
                }}
                onClick={async () => await handleStep1Next()}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {isEditMode ? "Save" : "Next"}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}
          {currentStep === 2 && !isViewMode && (
            <Button
              onClick={handleCreateProject}
              disabled={
                creating ||
                usersLoading ||
                selectedUsers.size === 0 ||
                newUserSubmitting
              }
              className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creating ? (
                <>
                  <InlineLoadSkeleton className="h-4 w-4 mr-2 rounded-full bg-white/30" />
                  {isAddUserMode ? "Saving..." : "Creating Project..."}
                </>
              ) : isAddUserMode ? (
                "Save Users"
              ) : (
                "Create Project"
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Add & Assign New User Nested Modal */}
      {showAddUserModal && (
        <div
          className="fixed inset-0  flex items-center justify-center z-60"
          style={{ background: "rgba(128, 128, 128, 0.3)", margin: "0px" }}
        >
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                Add New User
              </h3>
              <button
                onClick={handleCloseNewUserModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div className="space-y-3">
                <div>
                  <Label htmlFor="newUserFullName" className="text-gray-700">
                    Full Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="newUserFullName"
                    value={newUserFullName}
                    onChange={(e) => {
                      setNewUserFullName(e.target.value);
                      if (newUserErrors.full_name) {
                        setNewUserErrors((prev) => ({
                          ...prev,
                          full_name: "",
                        }));
                      }
                    }}
                    placeholder="Enter full name"
                    className={
                      newUserErrors.full_name ? "mt-1 border-red-300" : "mt-1"
                    }
                  />
                  {newUserErrors.full_name && (
                    <p className="mt-1 text-xs text-red-500">
                      {newUserErrors.full_name}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="newUserEmail" className="text-gray-700">
                    Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="newUserEmail"
                    type="email"
                    value={newUserEmail}
                    onChange={(e) => {
                      setNewUserEmail(e.target.value);
                      if (newUserErrors.email) {
                        setNewUserErrors((prev) => ({ ...prev, email: "" }));
                      }
                      if (newUserExistingWarning) {
                        setNewUserExistingWarning("");
                      }
                    }}
                    placeholder="user@example.com"
                    className={
                      newUserErrors.email ? "mt-1 border-red-300" : "mt-1"
                    }
                  />
                  {newUserErrors.email && (
                    <p className="mt-1 text-xs text-red-500">
                      {newUserErrors.email}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="newUserPhone" className="text-gray-700">
                    Phone (optional)
                  </Label>
                  <Input
                    id="newUserPhone"
                    type="tel"
                    value={newUserPhone}
                    onChange={(e) => setNewUserPhone(e.target.value)}
                    placeholder="Enter phone number"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="text-gray-700">
                    Client Role <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={newUserClientRoleId}
                    onValueChange={(value) => {
                      setNewUserClientRoleId(value);
                      if (newUserErrors.client_role) {
                        setNewUserErrors((prev) => ({
                          ...prev,
                          client_role: "",
                        }));
                      }
                    }}
                  >
                    <SelectTrigger
                      className={`mt-1 ${newUserErrors.client_role ? "border-red-300" : ""}`}
                    >
                      <SelectValue placeholder="Select client role" />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map((role) => (
                        <SelectItem key={role.role_id} value={role.role_id}>
                          {role.role_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {newUserErrors.client_role && (
                    <p className="mt-1 text-xs text-red-500">
                      {newUserErrors.client_role}
                    </p>
                  )}
                </div>

                <div>
                  <Label className="text-gray-700">
                    Project Role <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={newUserProjectRoleId}
                    onValueChange={(value) => {
                      setNewUserProjectRoleId(value);
                      if (newUserErrors.project_role) {
                        setNewUserErrors((prev) => ({
                          ...prev,
                          project_role: "",
                        }));
                      }
                    }}
                  >
                    <SelectTrigger
                      className={`mt-1 ${newUserErrors.project_role ? "border-red-300" : ""}`}
                    >
                      <SelectValue placeholder="Select project role" />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map((role) => (
                        <SelectItem key={role.role_id} value={role.role_id}>
                          {role.role_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {newUserErrors.project_role && (
                    <p className="mt-1 text-xs text-red-500">
                      {newUserErrors.project_role}
                    </p>
                  )}
                </div>

                <div className="flex items-center space-x-2 pt-1">
                  <Checkbox
                    id="newUserInvite"
                    checked={newUserInvite}
                    onCheckedChange={(checked) => setNewUserInvite(!!checked)}
                  />
                  <Label
                    htmlFor="newUserInvite"
                    className="cursor-pointer text-sm text-gray-700"
                  >
                    Send invite email to this user
                  </Label>
                </div>

                {newUserExistingWarning && (
                  <div className="mt-2 bg-amber-50 border border-amber-200 rounded-md p-2 text-xs text-amber-800 flex space-x-2">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{newUserExistingWarning}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end space-x-3 p-4 border-t bg-gray-50">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseNewUserModal}
                disabled={newUserSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleAddAndAssignNewUser}
                disabled={
                  newUserSubmitting ||
                  !newUserFullName.trim() ||
                  !newUserEmail.trim() ||
                  !newUserClientRoleId ||
                  !newUserProjectRoleId
                }
                className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {newUserSubmitting ? (
                  <>
                    <InlineLoadSkeleton className="h-4 w-4 mr-2 rounded-full bg-white/30" />
                    Adding...
                  </>
                ) : (
                  "Add & Assign User"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateProjectWizard;
